import { useMemo } from 'react'

import { useStatsigGate } from '@src/experiments/statsig-integration/use-statsig-gate.hook'

import { Cart, CartLineItem, PurchaseMode } from '../../../services/cartService/cart.service.types'
import useRecurringItemSubscriptionsExperiment from '../../useRecurringItemSubscriptionsExperiment'

export interface UseSubscriptionCartResult {
  isSubscriptionEnabled: boolean
  isInSubscriptionGate: boolean
  hasSubscriptionItems: boolean
  subscriptionLineItems: CartLineItem[]
  subscriptionEligibleItems: CartLineItem[]
  isSubscriptionItem: (lineItem: CartLineItem) => boolean
  isEligibleForSubscription: (lineItem: CartLineItem) => boolean
  shouldShowSubscribeNudge: boolean
}

export const useSubscriptionCart = (cart?: Cart | null): UseSubscriptionCartResult => {
  const isInSubscriptionGate = useStatsigGate('subscriptions_enabled')
  const isInExperimentBucket = useRecurringItemSubscriptionsExperiment()

  const lineItems = useMemo(() => cart?.lineItems ?? [], [cart])

  const subscriptionLineItems = useMemo(
    () => lineItems.filter(item => item.purchaseMode === PurchaseMode.RECURRING),
    [lineItems],
  )

  const hasSubscriptionItems = subscriptionLineItems.length > 0

  const subscriptionEligibleItems = useMemo(
    () =>
      lineItems.filter(
        item => item.purchaseMode !== PurchaseMode.RECURRING && item.variantMetaData?.isSubscribable === true,
      ),
    [lineItems],
  )

  const isSubscriptionEnabled = isInSubscriptionGate

  const shouldShowSubscribeNudge = isInSubscriptionGate && isInExperimentBucket

  const isSubscriptionItem = (lineItem: CartLineItem) => lineItem.purchaseMode === PurchaseMode.RECURRING

  const isEligibleForSubscription = (lineItem: CartLineItem) =>
    lineItem.purchaseMode !== PurchaseMode.RECURRING && lineItem.variantMetaData?.isSubscribable === true

  return {
    isSubscriptionEnabled,
    isInSubscriptionGate,
    hasSubscriptionItems,
    subscriptionLineItems,
    subscriptionEligibleItems,
    isSubscriptionItem,
    isEligibleForSubscription,
    shouldShowSubscribeNudge,
  }
}
